import {
  loadTranslations,
  storeTranslations,
  getCurrentStoredTranslationsVersion,
  storeTranslationsSelectedLocale,
  getSelectedLocale,
} from "../api/translationStorage";
import { getTranslationsVersion, getTranslations } from "../api/translations";

export const translations = {
  locales: {},
  version: null,
  selectedLocale: "en",
  supportedLanguages: [
    { key: "en", name: "EN", fullName: "english" },
    { key: "ja", name: "日本語", fullName: "japanese" },
  ],
  fallbackLocale: "en",
};

export const load = async () => {
  try {
    const currentVersion = await getCurrentStoredTranslationsVersion();
    let cachedLocales = await loadTranslations();
    let dbVersion = null;
    let source = "cache";

    try {
      dbVersion = await getTranslationsVersion();
    } catch (firebaseError) {
      console.warn("Translations Firebase unavailable, using cache if any", firebaseError?.message);
    }

    const hasCachedLocales = cachedLocales && Object.keys(cachedLocales).length > 0;
    const storedVersion = currentVersion != null ? Number(currentVersion) : null;
    const shouldFetch =
      dbVersion != null &&
      (!hasCachedLocales || !currentVersion || Number.isNaN(storedVersion) || dbVersion > storedVersion);

    if (shouldFetch) {
      try {
        const data = await getTranslations();
        if (data?.locales) {
          await storeTranslations({ version: String(dbVersion ?? data.version ?? 0), locales: data.locales });
          translations.locales = data.locales;
          translations.version = data.version ?? dbVersion;
          cachedLocales = data.locales;
          source = "firebase";
        }
      } catch (e) {
        console.warn("Failed to fetch translations from Firebase", e?.message);
        source = hasCachedLocales ? "cache_after_fetch_error" : "defaults_after_fetch_error";
      }
    } else if (!hasCachedLocales) {
      source = "defaults";
    }

    const selectedLocale = await getSelectedLocale();
    if (cachedLocales && Object.keys(cachedLocales).length) translations.locales = cachedLocales;
    if (selectedLocale) translations.selectedLocale = selectedLocale;
    translations.version = dbVersion ?? currentVersion ?? translations.version;

    const appTitle =
      translations.locales?.[translations.selectedLocale]?.pipeline_nav_app_title ??
      translations.locales?.[translations.selectedLocale]?.["pipeline.nav.app_title"] ??
      translations.locales?.[translations.fallbackLocale]?.pipeline_nav_app_title ??
      translations.locales?.[translations.fallbackLocale]?.["pipeline.nav.app_title"] ??
      "Video Dashboard Pro";

    console.info("[Translations] loaded", JSON.stringify({
      source,
      remoteVersion: dbVersion,
      localVersion: currentVersion,
      selectedLocale: translations.selectedLocale,
      appTitle,
      fetched: shouldFetch && source === "firebase",
    }));
    return true;
  } catch (e) {
    console.error("error loading translations", e);
    return false;
  }
};

export const setLocale = async (locale) => {
  translations.selectedLocale = locale;
  await storeTranslationsSelectedLocale(locale);
};

/** Firebase stores keys with "." replaced by "_"; support both for lookup */
const encodedKey = (k) => (k || "").replace(/\./g, "_");

export const getString = (key, defaultString = "", vars = {}) => {
  const localeMap = translations.locales?.[translations.selectedLocale];
  const fallbackMap = translations.locales?.[translations.fallbackLocale];
  const enc = encodedKey(key);
  let value =
    localeMap?.[enc] ?? localeMap?.[key] ?? fallbackMap?.[enc] ?? fallbackMap?.[key] ?? (defaultString || key);

  if (value == null || value === key || typeof value.replace !== "function") {
    value = value ?? defaultString ?? key;
  } else {
    value = value.replace(/\\n/g, "\n");
  }
  Object.keys(vars).forEach((k) => {
    value = String(value).replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), vars[k]);
  });
  return value;
};

export const isJP = () => translations.selectedLocale === "ja";
